/**
 * Connect — per-client push-notification flags editor (MVP).
 *
 * Each IoT client's anchor observation carries a `value` subcollection of ~113
 * "flag" docs ({category:"flag", subject:<name>, code:<value>, lastUpdated})
 * that drive its push notifications. This is a purpose-built replacement for
 * editing them by hand in Firefoo: list the flags, edit `code`, batch-write
 * back. The flag values are the per-client source of truth and stay in the
 * observation — this UI only reads/writes `code` (+ `lastUpdated`).
 *
 * Flags are shown as one alphabetically-sorted list (by name) with a live name
 * filter — the operator scans by name to find what they're configuring. Admin-
 * only (the connect panel is already admin-gated).
 */
import {
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  serverTimestamp,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import { resolveObservationRefs } from "./sensors";
import { escapeHtml } from "../utils/html";

interface FlagRow {
  path: string; // full doc path, for the write
  subject: string; // flag name
  code: string; // current value as a string (for the input)
  origIsNumber: boolean; // preserve numeric type on write-back
  lastUpdated: string;
}

/** Open the flags editor modal for a client's EoC. */
export async function openFlagsEditor(eocDocId: string, clientName: string): Promise<void> {
  const obsRefs = await resolveObservationRefs(eocDocId);
  const flags: FlagRow[] = [];
  for (const obsRef of obsRefs) {
    const snap = await getDocs(
      query(collection(obsRef, "value"), where("category", "==", "flag")),
    );
    for (const d of snap.docs) {
      const data = d.data();
      const rawCode = data.code;
      flags.push({
        path: d.ref.path,
        subject: String(data.subject ?? d.id),
        code: rawCode == null ? "" : String(rawCode),
        origIsNumber: typeof rawCode === "number",
        lastUpdated: data.lastUpdated ? String(data.lastUpdated).slice(0, 19) : "",
      });
    }
  }
  // Alphabetical by flag name — the operator scans the list by name to find
  // what they're configuring. Search filters this order live; it never re-sorts.
  flags.sort((a, b) => a.subject.localeCompare(b.subject));
  renderModal(clientName, flags);
}

function renderModal(clientName: string, flags: FlagRow[]): void {
  document.getElementById("flagsEditorOverlay")?.remove();

  const overlay = document.createElement("div");
  overlay.id = "flagsEditorOverlay";
  Object.assign(overlay.style, {
    position: "fixed", inset: "0", background: "rgba(17,24,39,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: "10000",
  });

  const panel = document.createElement("div");
  Object.assign(panel.style, {
    // Large by design: the operator reads the full list of names to find what
    // they're configuring (search is a secondary aid, not the primary path).
    background: "#fff", borderRadius: "10px", width: "min(1100px, 96vw)",
    height: "92vh", display: "flex", flexDirection: "column",
    boxShadow: "0 10px 40px rgba(0,0,0,0.3)", font: "14px system-ui, sans-serif",
  });

  // edited values, keyed by doc path
  const edits = new Map<string, string>();

  // One flat, alphabetically-sorted list. (Many names embed the deviceId, so the
  // alpha order naturally clusters a device's flags together anyway.)
  const rowsHtml = flags
    .map(
      (f) => `
      <div class="flag-row" data-subject="${escapeHtml(f.subject.toLowerCase())}"
           style="display:flex;gap:8px;align-items:center;padding:4px 12px;border-bottom:1px solid #f3f4f6">
        <label style="flex:1;min-width:0;color:#374151;overflow-wrap:anywhere" title="last updated ${escapeHtml(f.lastUpdated)}">
          ${escapeHtml(f.subject)}
        </label>
        <input type="text" data-path="${escapeHtml(f.path)}" value="${escapeHtml(f.code)}"
               style="width:160px;padding:4px 8px;border:1px solid #d1d5db;border-radius:4px;font-family:monospace">
      </div>`,
    )
    .join("");

  panel.innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;padding:12px 12px;border-bottom:1px solid #e5e7eb">
      <strong style="flex:1">Notification flags — ${escapeHtml(clientName)}</strong>
      <span id="flagsCount" style="color:#9ca3af">${flags.length} flags</span>
      <button id="flagsClose" type="button" aria-label="Close" style="border:none;background:none;font-size:17px;cursor:pointer;color:#6b7280">×</button>
    </div>
    <div style="padding:8px 12px;border-bottom:1px solid #e5e7eb">
      <input id="flagsSearch" type="search" placeholder="Filter the list by name (optional) — or just scroll"
             style="width:100%;padding:6px 8px;border:1px solid #d1d5db;border-radius:6px">
    </div>
    <div id="flagsList" style="overflow:auto;flex:1">${
      flags.length ? rowsHtml : '<p style="padding:16px;color:#9ca3af">No flags found for this client.</p>'
    }</div>
    <div style="display:flex;gap:8px;justify-content:flex-end;padding:12px 12px;border-top:1px solid #e5e7eb">
      <span id="flagsStatus" style="flex:1;color:#9ca3af;align-self:center"></span>
      <button id="flagsCancel" type="button" style="padding:6px 12px;border:1px solid #d1d5db;border-radius:6px;background:#fff;cursor:pointer">Cancel</button>
      <button id="flagsSave" type="button" disabled style="padding:6px 12px;border:none;border-radius:6px;background:#2563eb;color:#fff;cursor:pointer;opacity:0.5">Save</button>
    </div>`;

  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  const saveBtn = panel.querySelector<HTMLButtonElement>("#flagsSave")!;
  const status = panel.querySelector<HTMLElement>("#flagsStatus")!;
  const flagByPath = new Map(flags.map((f) => [f.path, f]));

  panel.querySelector("#flagsClose")?.addEventListener("click", close);
  panel.querySelector("#flagsCancel")?.addEventListener("click", close);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });

  // Live filter: show/hide rows by name as the user types. Pure visibility — the
  // alphabetical order of the list is untouched, so it's a fast narrow-down, not
  // a re-sort.
  panel.querySelector<HTMLInputElement>("#flagsSearch")?.addEventListener("input", (e) => {
    const q = (e.target as HTMLInputElement).value.toLowerCase().trim();
    panel.querySelectorAll<HTMLElement>(".flag-row").forEach((row) => {
      row.style.display = !q || (row.dataset.subject ?? "").includes(q) ? "" : "none";
    });
  });

  // track edits
  panel.querySelectorAll<HTMLInputElement>("input[data-path]").forEach((inp) => {
    inp.addEventListener("input", () => {
      const path = inp.dataset.path!;
      const orig = flagByPath.get(path)!.code;
      if (inp.value === orig) edits.delete(path);
      else edits.set(path, inp.value);
      const n = edits.size;
      saveBtn.disabled = n === 0;
      saveBtn.style.opacity = n === 0 ? "0.5" : "1";
      saveBtn.textContent = n === 0 ? "Save" : `Save ${n}`;
    });
  });

  saveBtn.addEventListener("click", async () => {
    if (edits.size === 0) return;
    saveBtn.disabled = true;
    status.textContent = "Saving…";
    try {
      const batch = writeBatch(db);
      for (const [path, value] of edits) {
        const row = flagByPath.get(path)!;
        const code =
          row.origIsNumber && value.trim() !== "" && !isNaN(Number(value))
            ? Number(value)
            : value;
        batch.set(doc(db, path), { code, lastUpdated: serverTimestamp() }, { merge: true });
      }
      await batch.commit();
      status.textContent = `Saved ${edits.size}.`;
      setTimeout(close, 700);
    } catch (err) {
      console.error("[connect] flag save failed", err);
      status.textContent = "Save failed — see console.";
      saveBtn.disabled = false;
    }
  });
}
